<script setup lang="ts">
import { computed } from 'vue'
import { useFrameStore } from '../stores/frameStore'
import FrameToolbar from './FrameToolbar.vue'
import ApocryphaFrame from './apocrypha/ApocryphaFrame.vue'
import type { Frame, FrameType } from '../types/frame'

const props = defineProps<{
  frame: Frame
  toolbarHeight: number
}>()

const frameStore = useFrameStore()

const frameTypeOptions: { value: FrameType; label: string }[] = [
  { value: 'bible-reading', label: '聖經閱讀' },
  { value: 'note-editor', label: '筆記編輯' },
  { value: 'keyword-search', label: '關鍵字搜尋' },
  { value: 'apocrypha-search', label: '次經查詢' },
  { value: 'map-lookup', label: '地圖查閱' },
]

const colorClasses: Record<Frame['color'], string> = {
  blue: 'bg-blue-100 border-blue-300',
  green: 'bg-green-100 border-green-300',
  orange: 'bg-orange-100 border-orange-300',
  purple: 'bg-purple-100 border-purple-300',
  red: 'bg-red-100 border-red-300',
  cyan: 'bg-cyan-100 border-cyan-300',
}

const frameStyle = computed(() => ({
  transform: `translate(${props.frame.x}px, ${props.frame.y}px)`,
  width: `${props.frame.w}px`,
  height: `${props.frame.h}px`,
}))

const isDragging = computed(() => frameStore.dragState?.frameId === props.frame.id)

const handleDragStart = (e: MouseEvent) => {
  frameStore.startDrag(props.frame.id, e)
}

const handleResizeStart = (e: MouseEvent) => {
  frameStore.startResize(props.frame.id, e)
  e.stopPropagation()
}

const handleRemove = () => {
  // 只從 frameStore 中刪除，不觸及數據庫
  // frames 在數據庫中的刪除由"記錄版面"功能控制
  frameStore.removeFrame(props.frame.id)
}

const handleTypeChange = (newType: FrameType) => {
  frameStore.updateFrameType(props.frame.id, newType)
}
</script>

<template>
  <div
    class="frame absolute"
    :data-frame-id="frame.id"
    :style="frameStyle"
    :class="{ 'dragging z-50 shadow-lg': isDragging }"
  >
    <div
      class="frame-title bg-gradient-to-r px-4 py-2 rounded-t-lg cursor-grab active:cursor-grabbing flex items-center justify-between"
      :class="colorClasses[frame.color]"
      @mousedown="handleDragStart"
    >
      <select
        class="frame-type-select"
        :value="frame.type"
        @change="(e) => handleTypeChange((e.target as HTMLSelectElement).value as FrameType)"
        @mousedown.stop
        @click.stop
        title="選擇功能框類型"
      >
        <option v-for="option in frameTypeOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>

      <!-- 筆記框顯示經文（僅當從聖經閱讀啟動時） -->
      <span v-if="frame.type === 'note-editor' && frame._noteInfo" class="frame-verse-info">
        {{ frame.title }}
      </span>

      <button
        class="frame-close-btn w-5 h-5 flex items-center justify-center rounded hover:bg-red-200 transition-colors flex-shrink-0"
        @click.stop="handleRemove"
        title="關閉框"
      >
        ✕
      </button>
    </div>

    <FrameToolbar v-if="frame.type !== 'apocrypha-search'" :type="frame.type" :frame-id="frame.frameId" :frame="frame" />

    <ApocryphaFrame v-if="frame.type === 'apocrypha-search'" :frame-id="frame.frameId" />

    <div
      class="frame-resize absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize"
      :style="{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }"
      @mousedown="handleResizeStart"
      title="調整大小"
    />
  </div>
</template>

<style scoped>
.frame {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  min-height: 100px;
  overflow: hidden;
}

.frame-title {
  cursor: grab;
  user-select: none;
  border-radius: 8px 8px 0 0;
}

.frame-title:active {
  cursor: grabbing;
}

.frame-type-select {
  padding: 4px 8px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.3);
  color: #333;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.frame-type-select:hover {
  background-color: rgba(255, 255, 255, 0.5);
  border-color: rgba(255, 255, 255, 0.8);
}

.frame-type-select:focus {
  outline: none;
  background-color: white;
  color: #333;
  border-color: rgba(0, 0, 0, 0.2);
}

.frame-verse-info {
  margin-left: 8px;
  font-size: var(--font-size-base);
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
}

.frame-content {
  flex: 1;
  overflow: auto;
}

.frame-close-btn {
  background-color: rgba(255, 255, 255, 0.3);
  border: 1px rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s;
}

.frame-close-btn:hover {
  background-color: rgba(255, 0, 0, 0.2);
  border-color: rgba(255, 0, 0, 0.5);
}

.frame-resize {
  cursor: nwse-resize;
}
</style>
